import type { ReactNode } from "react";
import ReactSelect from "react-select";
import type { StylesConfig } from "react-select";
import Field from "../Field/Field";
import Icon from "../Icon/Icon";
import "../Control/Control.css";
import "./SearchSelect.css";

export interface SearchSelectOption {
  value: string;
  label: string;
}

export interface SearchSelectProps {
  id: string;
  options: SearchSelectOption[];
  /** the selected value ("" for none) */
  value: string;
  onChange: (value: string) => void;
  label?: ReactNode;
  placeholder?: string;
  hint?: ReactNode;
  /** text shows under the field; `true` only turns the border red */
  error?: ReactNode | boolean;
  disabled?: boolean;
  /** shown when typing matches nothing */
  noOptionsText?: string;
  className?: string;
}

// react-select draws only the text and the menu; the box is .ds-control
const styles: StylesConfig<SearchSelectOption, false> = {
  // static so the menu is placed against the whole .ds-control box
  container: (base) => ({
    ...base,
    position: "static",
    flex: 1,
    minWidth: 0,
  }),
  control: () => ({ display: "flex", alignItems: "center", cursor: "pointer" }),
  valueContainer: (base) => ({ ...base, padding: 0 }),
  singleValue: (base) => ({ ...base, margin: 0, color: "var(--ink)" }),
  placeholder: (base) => ({ ...base, margin: 0, color: "var(--text-muted)" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "var(--ink)" }),
  indicatorsContainer: () => ({ display: "none" }),
  menu: (base) => ({
    ...base,
    top: "100%",
    left: 0,
    right: 0,
    width: "auto",
    marginTop: "var(--space-8)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-md)",
    boxShadow: "var(--shadow-dropdown)",
    overflow: "hidden",
    zIndex: 1200,
  }),
  option: (base, state) => ({
    ...base,
    color: "var(--ink)",
    fontSize: "var(--text-13)",
    background: state.isSelected
      ? "var(--lime-soft)"
      : state.isFocused
        ? "var(--surface-muted)"
        : "var(--surface)",
    cursor: "pointer",
  }),
};

/**
 * Dropdown you can type in to filter (long lists: provinces, towns).
 * Same box, label and error as TextField / Select.
 * Example: <SearchSelect id="city" label="City" options={opts} value={v} onChange={setV} />
 */
const SearchSelect = ({
  id,
  options,
  value,
  onChange,
  label,
  placeholder,
  hint,
  error,
  disabled = false,
  noOptionsText,
  className = "",
}: SearchSelectProps) => (
  <Field
    htmlFor={id}
    label={label}
    hint={hint}
    error={error as ReactNode}
    className={className}
  >
    <div
      className={`ds-control ds-control--md ds-search-select ${
        disabled ? "is-disabled" : ""
      }`}
    >
      <ReactSelect<SearchSelectOption, false>
        inputId={id}
        options={options}
        value={options.find((o) => o.value === value) ?? null}
        onChange={(option) => onChange(option?.value ?? "")}
        placeholder={placeholder}
        isDisabled={disabled}
        isSearchable
        noOptionsMessage={noOptionsText ? () => noOptionsText : undefined}
        styles={styles}
        classNamePrefix="ds-search-select"
        aria-invalid={error ? true : undefined}
      />
      <Icon
        name="chevronDown"
        size={18}
        className="ds-search-select__chevron"
      />
    </div>
  </Field>
);

export default SearchSelect;
